/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavParams } from '@euler/app/Routes';
import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { useCurrentStore } from '@euler/app/flows/auth/context';
import { OrderChatFab } from '@euler/app/flows/order/components/OrderDetailContent/OrderChatFab';
import { OrderDetailProvider } from '@euler/app/flows/order/components/OrderDetailProvider';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useOrderDetailPropsFromTaskContext } from '@euler/app/flows/task/functions/useOrderDetailPropsFromTaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useIsFocused$ } from '@euler/functions/useIsFocused$';
import { useRefreshFeedback } from '@euler/functions/useRefreshFeedback';
import { useServiceRecordCount } from '@euler/functions/useServiceRecordCount';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { InspectionTaskStatus } from '@euler/model/enum';
import {
  useBehaviorSubject,
  usePersistFn,
  useWatchValue,
} from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useEffect } from 'react';
import { useSharedValue } from 'react-native-reanimated';
import { Slot } from 'react-slot-fill';
import { filter, skip } from 'rxjs';
import { useTaskHeaderOptions } from '../../functions/useTaskHeaderOptions';
import { TaskDetailPageContent } from './components/Content';
import { TaskDetailPageContentForIpad } from './components/Content.iPad';
import { WiredTaskDetailPageContainer } from './components/DetailPageContainer';

export const TaskDetailScreen = wrapNavigatorScreen(
  () => {
    const appLoading = useAppLoading();
    const scrollY = useSharedValue(0);
    const playRefreshEffect = useRefreshFeedback();
    const isMobileLayout = useIsMobileLayout();

    const navigation =
      useNavigation<StackNavigationProp<TaskNavParams & AppNavParams>>();

    const {
      taskNo,
      detail,
      fetchDetail,
      taskManager,
      continueTo,
      continueParams,
      focused$,
    } = useTaskContext();

    useAIAssistantForPage('task_detail', { taskNo });

    const { event$ } = useOrderContext();
    const [state] = useBehaviorSubject(taskManager.state$);
    const basicInfo = detail.basicInfo;
    const extraInfo = useTaskExtraInfo();
    const subscribed = extraInfo?.observes.length;
    const canceled = basicInfo.status === InspectionTaskStatus.Cancelled;
    const orderDetailProps = useOrderDetailPropsFromTaskContext();
    const store = useCurrentStore();

    const { state: serviceRecordCount = 0 } = useServiceRecordCount(
      { licensePlateNo: basicInfo.licensePlateNo, vin: basicInfo.vin },
      {
        key: `service-records/counts/${taskNo}.json`,
        ttl: 0,
      },
    );

    useWatchValue(subscribed, (prev, curr) => {
      if (prev == null || curr == null) return;
      if (!prev && curr) {
        event$.next({
          type: 'subscribed',
          orderNo: detail.basicInfo.orderNo,
          taskNo: detail.basicInfo.taskNo,
          isMainOrder: detail.basicInfo.isMainOrder,
        });
      } else if (prev && !curr) {
        event$.next({
          type: 'unsubscribed',
          orderNo: detail.basicInfo.orderNo,
          taskNo: detail.basicInfo.taskNo,
          isMainOrder: detail.basicInfo.isMainOrder,
        });
      }
    });

    useEffect(() => {
      return navigation.addListener('beforeRemove', () => {
        taskManager.end();
      });
    }, [navigation, taskManager]);

    const { showActions } = useTaskHeaderOptions({
      basicInfo,
      shareToken: extraInfo?.shareToken,
      canCreateChildOrder: store != null,
      onRefresh: () => taskManager.refresh(true),
    });

    const onRefresh = useCallback(async () => {
      await fetchDetail();
      void playRefreshEffect();
    }, [fetchDetail, playRefreshEffect]);

    const startSamplingCheck = usePersistFn(() => {
      void appLoading.showWithAction(async () => {
        if (continueParams?.edit) {
          await taskManager.samplingCheckManager.requestAmend();
        } else {
          await taskManager.samplingCheckManager.begin();
        }
        await taskManager.samplingCheckManager.prepare();
        navigation.push('_samplingCheck', {});
      });
    });

    useEffect(() => {
      if (continueTo === '_samplingCheck') {
        startSamplingCheck();
      }
    }, [continueTo, startSamplingCheck]);

    const pageFocused$ = useIsFocused$();

    useEffect(() => {
      const sub = pageFocused$
        .pipe(
          filter(x => x),
          skip(1), // skip the first screen appearing
        )
        .subscribe(() => {
          focused$.next();
          // sync task detail each time the screen is focused,
          // except for the first time the screen appears
          void taskManager.sync();
        });
      return () => sub.unsubscribe();
    }, [focused$, pageFocused$, taskManager]);

    return (
      <OrderDetailProvider props={orderDetailProps}>
        <WiredTaskDetailPageContainer
          isRefreshing={state.status === 'loading' && state.refreshing}
          onRefresh={onRefresh}
          titleLoadingIndicator={
            state.status === 'loading' && !state.refreshing
          }
          onShowActions={canceled ? undefined : showActions}
          scrollY={scrollY}
          subTitle={
            detail.basicInfo.status === InspectionTaskStatus.Finished
              ? detail.basicInfo.isMainOrder
                ? '本次服务已完成'
                : '子订单已完成'
              : undefined
          }
        >
          {isMobileLayout ? (
            <TaskDetailPageContent
              scrollY={scrollY}
              serviceRecordCount={serviceRecordCount}
            />
          ) : (
            <TaskDetailPageContentForIpad
              scrollY={scrollY}
              serviceRecordCount={serviceRecordCount}
              onRefresh={onRefresh}
            />
          )}
        </WiredTaskDetailPageContainer>
        <Slot name={`task-detail-bottom-action-${taskNo}`} />
        <OrderChatFab orderNo={detail.basicInfo.orderNo} />
      </OrderDetailProvider>
    );
  },
  { headerShown: false },
);
